import React from 'react';

import type { ArtStyleOption, ColorOption, Theme } from '../../lib/domain-types';
import { MOBILE } from '../mobile.tokens';
import { NewBadge } from '../../steps/design/components/NewBadge';
import {
  CardButton,
  SelectRing,
  ThemeFace,
  TileBadge,
  ColorFace,
  ColorAccent,
  StyleFace,
  FaceImg,
} from './MobileDesignFlow/MobileDesignFlow.styles';

/**
 * Shared theme / colour / art-style carousel cards for the v0 mobile design flows
 * (Theme step + Colors & Art Style step). Extracted from v3's single MobileDesignFlow so
 * the two split steps can render the same cards without duplicating them.
 */

export const PlayIcon = (): React.ReactElement => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      transform="translate(3.33 1.67)"
      d="M14.5071 6.12715C16.2754 7.08872 16.2754 9.57794 14.5071 10.5395L3.83051 16.3454C2.11196 17.28 0 16.0636 0 14.1393L0 2.52741C0 0.603069 2.11196 -0.613314 3.83051 0.32123L14.5071 6.12715Z"
      fill={MOBILE.white}
    />
  </svg>
);

const CardShell = ({
  selected,
  width,
  children,
  onClick,
  label,
  gaId,
}: {
  selected: boolean;
  width: number;
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  gaId: string;
}): React.ReactElement => (
  <CardButton type="button" onClick={onClick} data-ga-id={gaId} aria-label={label} $selected={selected} $width={width}>
    {children}
    {selected ? <SelectRing /> : null}
  </CardButton>
);

export const ThemeCard = ({
  theme,
  selected,
  onClick,
}: {
  theme: Theme;
  selected: boolean;
  onClick: () => void;
}): React.ReactElement => (
  <CardShell selected={selected} width={70} onClick={onClick} label={theme.name} gaId={`cfg-mobile-design-theme-${theme.id}`}>
    <ThemeFace>{theme.logo ? <FaceImg src={theme.logo} alt="" /> : null}</ThemeFace>
    {theme.isNew ? (
      <TileBadge>
        <NewBadge small />
      </TileBadge>
    ) : null}
  </CardShell>
);

export const ColorCard = ({
  color,
  selected,
  onClick,
}: {
  color: ColorOption;
  selected: boolean;
  onClick: () => void;
}): React.ReactElement => {
  const inner = selected
    ? { left: 20.44, top: 26.47, w: 55.91, h: 52.17, radius: 16.25 }
    : { left: 17.88, top: 23.16, w: 48.92, h: 45.65, radius: 14.22 };
  return (
    <CardShell selected={selected} width={70} onClick={onClick} label={color.name} gaId={`cfg-mobile-design-color-${color.id}`}>
      <ColorFace $bg={color.bg}>
        <ColorAccent
          $bg={color.corner}
          $left={inner.left}
          $top={inner.top}
          $w={inner.w}
          $h={inner.h}
          $radius={inner.radius}
        />
      </ColorFace>
    </CardShell>
  );
};

export const StyleCard = ({
  style,
  selected,
  onClick,
}: {
  style: ArtStyleOption;
  selected: boolean;
  onClick: () => void;
}): React.ReactElement => (
  <CardShell selected={selected} width={108} onClick={onClick} label={style.name} gaId={`cfg-mobile-design-style-${style.id}`}>
    <StyleFace>{style.image ? <FaceImg src={style.image} alt="" /> : null}</StyleFace>
  </CardShell>
);
